
import { useRef, useState } from "react";
import {
  addDoc,
  collection,
  serverTimestamp,
  getDocs,
  query,
  where,
  limit,
} from "firebase/firestore";
import { db } from "../../firebase";

const initialForm = {
  patientName: "",
  patientId: "",
  mobile: "",
  q1: "",
  q2: [],
  q2Other: "",
  q3: "",
  q4: "",
  q5: "",
  q6: "",
  q7: "",
  q8: "",
  q9: [],
  q9Other: "",
  q10: "",
  suggestions: "",
  signature: "",
};

const questions = [
  {
    en: "How would you rate your overall experience?",
    te: "మీ మొత్తం అనుభవాన్ని ఎలా రేట్ చేస్తారు?",
    type: "rating",
    options: ["Excellent", "Good", "Average", "Poor"],
  },
  {
    en: "How did you get to know about this clinic?",
    te: "ఈ క్లినిక్ గురించి మీకు ఎలా తెలిసింది?",
    type: "checkbox",
    options: [
      "Referred by Friends & Family",
      "Digital Platform (FB/Insta/Others)",
      "Referred by Consultant",
      "Previous Patient",
      "Others",
    ],
  },
  {
    en: "How comfortable are you with the registration process & how long did it take?",
    te: "రిజిస్ట్రేషన్ ప్రక్రియ మీకు ఎంత సౌకర్యవంతంగా అనిపించింది?",
    type: "radio",
    options: ["Less than 20 minutes", "More than 30 minutes"],
  },
  {
    en: "How satisfied are you with the information provided by the Staff & PRE's?",
    te: "సిబ్బంది మరియు PREలు అందించిన సమాచారంతో మీరు ఎంత సంతృప్తిగా ఉన్నారు?",
    type: "rating",
    options: ["Excellent", "Very Good", "Fair", "Poor"],
  },
  {
    en: "How long did you wait to meet the physiotherapist?",
    te: "ఫిజియోథెరపిస్ట్‌ను కలవడానికి మీరు ఎంతసేపు వేచి ఉన్నారు?",
    type: "radio",
    options: [
      "Within 15–20 minutes",
      "Within 25 minutes & more",
    ],
  },
  {
    en: "How satisfied are you with the treatment & the way the physiotherapist attended your problem?",
    te: "చికిత్స మరియు ఫిజియోథెరపిస్ట్ మీ సమస్యను పరిష్కరించిన విధానంతో మీరు ఎంత సంతృప్తిగా ఉన్నారు?",
    type: "rating",
    options: ["Excellent", "Very Good", "Fair", "Poor"],
  },
  {
    en: "How satisfied are you with the cleanliness in the waiting area & washroom?",
    te: "వెయిటింగ్ ఏరియా మరియు వాష్‌రూమ్ శుభ్రతతో మీరు ఎంత సంతృప్తిగా ఉన్నారు?",
    type: "radio",
    options: [
      "Very Clean",
      "Not Very Clean",
      "Not at All Clean",
      "I Didn't Use",
    ],
  },
  {
    en: "How satisfied are you with the patient's transportation by the housekeeping staff?",
    te: "హౌస్‌కీపింగ్ సిబ్బంది రోగిని తరలించిన విధానంతో మీరు ఎంత సంతృప్తిగా ఉన్నారు?",
    type: "radio",
    options: [
      "Excellent",
      "Very Good",
      "Poor",
      "I Didn't Use",
    ],
  },
  {
    en: "Most important area in the hospital where services can be improved?",
    te: "ఆసుపత్రిలో సేవలను మెరుగుపరచాల్సిన ముఖ్యమైన విభాగం ఏది?",
    type: "checkbox",
    options: [
      "Waiting Time at OPD",
      "Doctor Services",
      "Staff Services",
      "Others (Specify)",
    ],
  },
  {
    en: "How likely are you to recommend this clinic?",
    te: "ఈ క్లినిక్‌ను ఇతరులకు సిఫార్సు చేసే అవకాశం ఎంత ఉంది?",
    type: "radio",
    options: [
      "Very Likely",
      "Somewhat Likely",
      "Never",
    ],
  },
];

const translations = {
  Excellent: "అద్భుతం",
  Good: "బాగుంది",
  Average: "సగటు",
  Poor: "బాగోలేదు",
  "Very Good": "చాలా మంచిది",
  Fair: "పర్వాలేదు",
  "Referred by Friends & Family": "స్నేహితులు మరియు కుటుంబ సభ్యుల ద్వారా",
  "Digital Platform (FB/Insta/Others)": "సోషల్ మీడియా ద్వారా",
  "Referred by Consultant": "కన్సల్టెంట్ ద్వారా",
  "Previous Patient": "గతంలో వచ్చిన రోగి",
  Others: "ఇతరులు",
  "Less than 20 minutes": "20 నిమిషాల కంటే తక్కువ",
  "More than 30 minutes": "30 నిమిషాల కంటే ఎక్కువ",
  "Within 15–20 minutes": "15–20 నిమిషాలలోపు",
  "Within 25 minutes & more": "25 నిమిషాలు లేదా అంతకంటే ఎక్కువ",
  "Very Clean": "చాలా శుభ్రంగా ఉంది",
  "Not Very Clean": "పూర్తిగా శుభ్రంగా లేదు",
  "Not at All Clean": "అస్సలు శుభ్రంగా లేదు",
  "I Didn't Use": "నేను ఉపయోగించలేదు",
  "Waiting Time at OPD": "OPD వద్ద వేచి ఉండే సమయం",
  "Doctor Services": "డాక్టర్ సేవలు",
  "Staff Services": "సిబ్బంది సేవలు",
  "Others (Specify)": "ఇతరులు",
  "Very Likely": "తప్పకుండా సిఫార్సు చేస్తాను",
  "Somewhat Likely": "సిఫార్సు చేసే అవకాశం ఉంది",
  Never: "సిఫార్సు చేయను",
};

const emojis = ["😍", "😊", "😐", "🙁"];

const getInitialForm = () => ({
  ...initialForm,
});

export default function PatientFeedback() {
  const [form, setForm] = useState(getInitialForm);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [language, setLanguage] = useState("en");
  const [fieldErrors, setFieldErrors] = useState({});
  const [fetchingPatient, setFetchingPatient] = useState(false);
  const [patientLookupStatus, setPatientLookupStatus] = useState("");
  const [lookupInput, setLookupInput] = useState("");
  const [lookupResults, setLookupResults] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);

  const t = (en, te) => language === "te" ? te : en;

  const translateOption = (option) =>
    language === "te" ? translations[option] || option : option;

  const getCanvasPoint = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    return {
      x: (event.clientX - rect.left) * (canvas.width / rect.width),
      y: (event.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startDrawing = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    event.preventDefault();
    canvas.setPointerCapture?.(event.pointerId);

    const ctx = canvas.getContext("2d");
    const point = getCanvasPoint(event);

    isDrawing.current = true;
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  };

  const drawSignature = (event) => {
    if (!isDrawing.current || !canvasRef.current) return;

    event.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const point = getCanvasPoint(event);

    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#172554";
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const stopDrawing = (event) => {
    if (!isDrawing.current) return;

    isDrawing.current = false;
    if (canvasRef.current) {
      updateField("signature", canvasRef.current.toDataURL("image/png"));
      if (event?.pointerId !== undefined) {
        try {
          canvasRef.current.releasePointerCapture?.(event.pointerId);
        } catch (_) {
          // Pointer capture may already have been released by the browser.
        }
      }
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    isDrawing.current = false;
    updateField("signature", "");
  };

  const resetSignatureCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    isDrawing.current = false;
  };

  const inputClass =
    "block w-full min-w-0 max-w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-[#204080] focus:ring-2 focus:ring-blue-100";

  const updateField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));

    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const applyPatientDetails = (patient) => {
    const getValue = (...keys) => {
      for (const key of keys) {
        const value = patient?.[key];
        if (value !== undefined && value !== null && value !== "") return value;
      }
      return "";
    };

    const patientId = String(
      getValue("vpjc_id", "vpjcId")
    ).trim().toUpperCase();
    const patientName = getValue("name", "patient_name", "patientName");
    const mobile = String(
      getValue("mobile", "residentialMobile", "residential_mobile")
    ).trim();

    setForm((prev) => ({
      ...prev,
      patientId: patientId || prev.patientId,
      patientName: patientName || "",
      mobile: mobile || "",
    }));
    setLookupInput(patientId || lookupInput);
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.patientId;
      delete next.patientName;
      delete next.mobile;
      return next;
    });
    setLookupResults([]);
    setPatientLookupStatus("Patient details fetched successfully.");
  };

  const fetchPatientDetails = async () => {
    const value = lookupInput.trim();
    const isId = /^VPJC-\d{6}$/i.test(value);
    const isMobile = /^[6-9]\d{9}$/.test(value);

    if (!isId && !isMobile) {
      setLookupResults([]);
      setPatientLookupStatus(
        "Enter a valid VPJC ID or 10-digit mobile number."
      );
      return;
    }

    try {
      setFetchingPatient(true);
      setLookupResults([]);
      setPatientLookupStatus("Searching patients...");

      const searchValue = isId ? value.toUpperCase() : value;
      const patientQuery = query(
        collection(db, "patients"),
        where(isId ? "vpjc_id" : "mobile", "==", searchValue)
      );
      const patientSnapshot = await getDocs(patientQuery);

      let patientRecords = patientSnapshot.docs.map((patientDoc) => ({
        _patientDocId: patientDoc.id,
        ...patientDoc.data(),
      }));

      // Search legacy visit records when no patient profile is found.
      if (patientRecords.length === 0) {
        const legacyVisitQuery = query(
          collection(db, "visits"),
          where(isId ? "vpjc_id" : "mobile", "==", searchValue)
        );
        const legacyVisits = await getDocs(legacyVisitQuery);
        const uniquePatients = new Map();

        legacyVisits.docs.forEach((visitDoc) => {
          const visit = visitDoc.data();
          const key = visit.vpjc_id || visit.patient_id || visitDoc.id;
          if (!uniquePatients.has(key)) {
            uniquePatients.set(key, {
              ...visit,
              _patientDocId: visit.patient_id || key,
            });
          }
        });
        patientRecords = Array.from(uniquePatients.values());
      }

      if (patientRecords.length === 0) {
        setPatientLookupStatus(
          "No patient found. Please check the ID or mobile number."
        );
        return;
      }

      // Merge the detailed registration record when one is available.
      const candidates = await Promise.all(
        patientRecords.map(async (base) => {
          const baseVpjcId = base.vpjc_id || base.vpjcId;
          if (!baseVpjcId) return base;

          const registrationQuery = query(
            collection(db, "patients_regisgistration_form"),
            where("vpjc_id", "==", baseVpjcId),
            limit(1)
          );
          const registrationSnapshot = await getDocs(registrationQuery);
          if (registrationSnapshot.empty) return base;

          const registrationDoc = registrationSnapshot.docs[0];
          return {
            ...base,
            ...registrationDoc.data(),
            _patientDocId: base._patientDocId,
            _registrationDocId: registrationDoc.id,
          };
        })
      );

      if (candidates.length === 1) {
        applyPatientDetails(candidates[0]);
      } else {
        setLookupResults(candidates);
        setPatientLookupStatus(
          "Multiple records found. Please select the correct patient."
        );
      }
    } catch (error) {
      console.error("Patient search error:", error);
      setPatientLookupStatus(
        "Unable to fetch patient details. Please try again."
      );
    } finally {
      setFetchingPatient(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (saving || fetchingPatient) return;

    setMessage("");

    const errors = {};

    if (!form.patientId.trim()) {
      errors.patientId = t(
        "Please enter your Patient VPJC ID.",
        "దయచేసి VPJC రోగి ఐడిని నమోదు చేయండి."
      );
    }

    if (!form.patientName.trim()) {
      errors.patientName = t(
        "Please fetch your patient details first.",
        "ముందుగా మీ రోగి వివరాలను పొందండి."
      );
    }

    if (
      !form.mobile.trim() ||
      !/^[6-9]\d{9}$/.test(form.mobile.trim())
    ) {
      errors.mobile = t(
        "Enter a valid 10-digit mobile number.",
        "సరైన 10 అంకెల మొబైల్ నంబర్ నమోదు చేయండి."
      );
    }

    questions.forEach((question, index) => {
      const field = `q${index + 1}`;
      const value = form[field];

      const empty = Array.isArray(value)
        ? value.length === 0
        : !value;

      if (empty) {
        errors[field] = t(
          "Please select an answer.",
          "దయచేసి ఒక సమాధానాన్ని ఎంచుకోండి."
        );
      }
    });

    if (form.q2.includes("Others") && !form.q2Other.trim()) {
      errors.q2Other = t(
        "Please specify how you heard about the clinic.",
        "క్లినిక్ గురించి ఎలా తెలిసిందో వివరించండి."
      );
    }

    if (form.q9.includes("Others (Specify)") && !form.q9Other.trim()) {
      errors.q9Other = t(
        "Please specify the area for improvement.",
        "మెరుగుపరచాల్సిన విభాగాన్ని వివరించండి."
      );
    }

    if (!form.signature) {
      errors.signature = t(
        "Please provide your signature.",
        "దయచేసి మీ సంతకాన్ని అందించండి."
      );
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setMessageType("error");
      setMessage(
        t(
          "Please complete all required fields.",
          "దయచేసి తప్పనిసరి వివరాలన్నింటినీ నమోదు చేయండి."
        )
      );
      return;
    }

    setSaving(true);

    try {
      await addDoc(collection(db, "patient_feedback"), {
        patient_name: form.patientName.trim(),
        patient_id: form.patientId.trim().toUpperCase(),
        mobile: form.mobile.trim(),
        suggestions: form.suggestions.trim(),
        patient_signature: form.signature,
        other_details: {
          referral_source: form.q2Other.trim(),
          improvement_area: form.q9Other.trim(),
        },
        responses: Object.fromEntries(
          questions.map((_, index) => [
            `q${index + 1}`,
            form[`q${index + 1}`],
          ])
        ),
        created_at: serverTimestamp(),
      });

      setMessage("");
      setForm(getInitialForm());
      setLookupInput("");
      setLookupResults([]);
      resetSignatureCanvas();
      setFieldErrors({});
      setPatientLookupStatus("");
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Feedback submission failed:", error);

      setMessageType("error");
      setMessage(
        t(
          "Unable to submit feedback. Please try again.",
          "అభిప్రాయాన్ని సమర్పించలేకపోయాము. దయచేసి మళ్లీ ప్రయత్నించండి."
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setForm(getInitialForm());
    setLookupInput("");
    setLookupResults([]);
    resetSignatureCanvas();
    setMessage("");
    setFieldErrors({});
    setPatientLookupStatus("");
  };

  const getLookupMessage = () => {
    if (!patientLookupStatus) return null;

    const success = patientLookupStatus.includes("successfully");
    const searching = patientLookupStatus.includes("Searching");
    const multiple = patientLookupStatus.includes("Multiple records");
    let translatedMessage = patientLookupStatus;

    if (language === "te") {
      if (patientLookupStatus.includes("valid VPJC ID")) {
        translatedMessage = "సరైన VPJC ID లేదా 10 అంకెల మొబైల్ నంబర్ నమోదు చేయండి.";
      } else if (patientLookupStatus.includes("No patient found")) {
        translatedMessage = "రోగి వివరాలు కనుగొనబడలేదు. ID లేదా మొబైల్ నంబర్‌ను తనిఖీ చేయండి.";
      } else if (patientLookupStatus.includes("Multiple records")) {
        translatedMessage = "ఒకటి కంటే ఎక్కువ రికార్డులు ఉన్నాయి. సరైన రోగిని ఎంచుకోండి.";
      } else if (patientLookupStatus.includes("Unable")) {
        translatedMessage = "రోగి వివరాలను పొందలేకపోయాము. దయచేసి మళ్లీ ప్రయత్నించండి.";
      } else if (searching) {
        translatedMessage = "రోగి వివరాలు వెతుకుతున్నాము...";
      } else if (success) {
        translatedMessage = "రోగి వివరాలు విజయవంతంగా పొందబడ్డాయి.";
      }
    }

    return (
      <p
        role={success || searching || multiple ? "status" : "alert"}
        className={`mt-2 break-words text-xs font-medium ${
          success ? "text-green-600" : searching || multiple ? "text-[#204080]" : "text-red-600"
        }`}
      >
        {success ? "✓ " : searching || multiple ? "" : "⚠ "}
        {translatedMessage}
      </p>
    );
  };

  return (
    <div className="min-h-screen w-full min-w-0 overflow-x-clip bg-[#F5F7FB]">

      {/* HEADER */}
      <header className="sticky top-0 z-40 w-full border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur-md">
        <div className="mx-auto flex w-full max-w-5xl min-w-0 flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-6">

          <div className="flex min-w-0 items-center gap-2.5 sm:gap-3">
            <img
              src="/VPJCLogo-New.png"
              alt="VPJC Logo"
              className="h-11 w-11 shrink-0 object-contain sm:h-14 sm:w-14"
            />

            <div className="min-w-0 flex-1">
              <p className="break-words text-base font-bold leading-tight tracking-tight text-[#204080] sm:text-xl">
                Visakha Physiotherapy & Jasti Chiropractic
              </p>

              <h1 className="mt-1 break-words text-xs font-medium leading-normal text-slate-500 sm:text-sm">
                {t("Patient Feedback", "రోగి అభిప్రాయం")}
              </h1>
            </div>
          </div>

          <div className="flex w-full shrink-0 justify-end sm:w-auto">
            <div className="flex items-center rounded-xl border border-slate-200 bg-slate-50 p-1">
              <button
                type="button"
                onClick={() => setLanguage("en")}
                className={`min-h-9 rounded-lg px-3 py-2 text-xs font-semibold transition sm:px-4 ${
                  language === "en"
                    ? "bg-[#204080] text-white shadow-sm"
                    : "text-slate-500 hover:bg-white"
                }`}
              >
                English
              </button>

              <button
                type="button"
                onClick={() => setLanguage("te")}
                className={`min-h-9 rounded-lg px-3 py-2 text-xs font-semibold transition sm:px-4 ${
                  language === "te"
                    ? "bg-[#204080] text-white shadow-sm"
                    : "text-slate-500 hover:bg-white"
                }`}
              >
                తెలుగు
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* MAIN CONTENT */}
      <main className="mx-auto w-full max-w-5xl min-w-0 px-3 py-5 pb-36 sm:px-6 sm:py-8 sm:pb-32">

        <div className="mb-6">
          <p className="max-w-2xl break-words text-sm leading-6 text-slate-500">
            {t(
              "Share your experience with Visakha Physiotherapy & Jasti Chiropractic Centre. Your feedback helps us improve our services.",
              "విశాఖ ఫిజియోథెరపీ & జాస్తి చిరోప్రాక్టిక్ సెంటర్‌లో మీ అనుభవాన్ని పంచుకోండి. మీ అభిప్రాయం మా సేవలను మెరుగుపరచడానికి సహాయపడుతుంది."
            )}
          </p>
        </div>

        <form
          id="patient-feedback-form"
          noValidate
          onSubmit={handleSubmit}
          className="min-w-0 space-y-5"
        >

         {/* PATIENT DETAILS */}
          <section className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">

            <div className="mb-5 flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-sm font-bold text-[#204080]">
                01
              </div>

              <div className="min-w-0">
                <h2 className="font-bold text-slate-900">
                  {t("Patient Details", "రోగి వివరాలు")}
                </h2>
                <p className="text-xs text-slate-500">
                  {t(
                    "Enter your details below.",
                    "మీ వివరాలను నమోదు చేయండి."
                  )}
                </p>
              </div>
            </div>

            <div className="grid w-full min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">

              {/* PATIENT ID */}
              <div className="min-w-0 sm:col-span-2">
                <label className="mb-2 block text-sm font-semibold text-slate-700">
                  {t("VPJC ID or Mobile Number", "VPJC ID లేదా మొబైల్ నంబర్")}
                  <span className="ml-1 text-red-500">*</span>
                </label>

                <div className="flex min-w-0 flex-col gap-2 sm:flex-row">
                  <input
                    className={inputClass}
                    value={lookupInput}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      setLookupInput(value);
                      setLookupResults([]);
                      setPatientLookupStatus("");
                      setForm((prev) => ({
                        ...prev,
                        patientId: "",
                        patientName: "",
                        mobile: "",
                      }));
                      setFieldErrors((prev) => {
                        const next = { ...prev };
                        delete next.patientId;
                        delete next.patientName;
                        delete next.mobile;
                        return next;
                      });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        fetchPatientDetails();
                      }
                    }}
                    placeholder={t("Enter VPJC ID or 10-digit mobile number", "VPJC ID లేదా 10 అంకెల మొబైల్ నంబర్ నమోదు చేయండి")}
                  />

                  <button
                    type="button"
                    disabled={fetchingPatient || !lookupInput.trim()}
                    onClick={fetchPatientDetails}
                    className="min-h-11 w-full shrink-0 rounded-xl bg-[#204080] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#2E5AAC] disabled:opacity-50 sm:w-auto"
                  >
                    {fetchingPatient
                      ? t("Searching...", "వెతుకుతోంది...")
                      : t("Find Patient", "రోగిని కనుగొనండి")}
                  </button>
                </div>

                {fieldErrors.patientId && (
                  <p className="mt-1 text-xs text-red-600">
                    {fieldErrors.patientId}
                  </p>
                )}

                {getLookupMessage()}

                {lookupResults.length > 0 && (
                  <div className="mt-4 space-y-2" role="group" aria-label={t("Select patient", "రోగిని ఎంచుకోండి")}>
                    {lookupResults.map((patient) => {
                      const candidateId = patient.vpjc_id || patient.vpjcId || "";
                      const candidateName = patient.name || patient.patient_name || "Unnamed patient";
                      const candidateMobile = patient.mobile || patient.residentialMobile || patient.residential_mobile || "No mobile";

                      return (
                        <button
                          key={patient._patientDocId || candidateId || `${candidateName}-${candidateMobile}`}
                          type="button"
                          onClick={() => applyPatientDetails(patient)}
                          className="w-full rounded-xl border border-slate-200 bg-white p-3 text-left transition hover:border-[#204080] hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        >
                          <span className="block font-semibold text-slate-800">{candidateName}</span>
                          <span className="mt-1 block text-xs text-slate-500">
                            {candidateId || "No VPJC ID"} · {candidateMobile}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* PATIENT NAME */}
              <div className="min-w-0">
                <label className="mb-2 block text-sm font-semibold text-slate-700">
                  {t("Patient Name", "రోగి పేరు")} *
                </label>

                <input
                  className={`${inputClass} bg-slate-50`}
                  value={form.patientName}
                  readOnly
                  placeholder={t(
                    "Automatically populated",
                    "ఆటోమేటిక్‌గా నింపబడుతుంది"
                  )}
                />

                {fieldErrors.patientName && (
                  <p className="mt-1 text-xs text-red-600">
                    {fieldErrors.patientName}
                  </p>
                )}
              </div>

              {/* MOBILE */}
              <div className="min-w-0">
                <label className="mb-2 block text-sm font-semibold text-slate-700">
                  {t("Mobile Number", "మొబైల్ నంబర్")} *
                </label>

                <input
                  type="tel"
                  className={`${inputClass} bg-slate-50`}
                  value={form.mobile}
                  readOnly
                  placeholder={t(
                    "Automatically populated",
                    "ఆటోమేటిక్‌గా నింపబడుతుంది"
                  )}
                />

                {fieldErrors.mobile && (
                  <p className="mt-1 text-xs text-red-600">
                    {fieldErrors.mobile}
                  </p>
                )}
              </div>

            </div>
          </section>
          
          {/* EXPERIENCE */}
          <section className="min-w-0 rounded-2xl border border-slate-200 bg-[#F7F9FD] p-3 sm:p-6">

            <div className="mb-5 flex min-w-0 items-center gap-3 px-1">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-sm font-bold text-[#204080]">
                02
              </div>

              <div className="min-w-0">
                <h2 className="break-words font-bold text-slate-900">
                  {t("Your Experience", "మీ అనుభవం")}
                </h2>

                <p className="text-xs text-slate-500">
                  {t(
                    "Please answer all the questions.",
                    "దయచేసి అన్ని ప్రశ్నలకు సమాధానం ఇవ్వండి."
                  )}
                </p>
              </div>
            </div>

            <div className="min-w-0 space-y-3">
              {questions.map((question, index) => {
                const field = `q${index + 1}`;
                const isRating = question.type === "rating";
                const isCheckbox = question.type === "checkbox";

                const selectedValues = isCheckbox
                  ? form[field]
                  : [];

                return (
                  <fieldset
                    key={field}
                    className="min-w-0 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-5"
                  >
                    <legend className="sr-only">
                      {language === "te"
                        ? question.te
                        : question.en}
                    </legend>

                    <div className="flex min-w-0 items-start gap-2.5">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-[11px] font-bold text-[#204080]">
                        {index + 1}
                      </span>

                      <h3 className="min-w-0 flex-1 break-words text-sm font-semibold leading-6 text-slate-800">
                        {language === "te"
                          ? question.te
                          : question.en}
                        <span className="ml-1 text-red-500">*</span>
                      </h3>
                    </div>

                    <div
                      className={`mt-4 grid w-full min-w-0 gap-2 ${
                        isRating
                          ? "grid-cols-2 sm:grid-cols-4"
                          : "grid-cols-1 sm:grid-cols-2"
                      }`}
                    >
                      {question.options.map((option, optionIndex) => {
                        const selected = isCheckbox
                          ? selectedValues.includes(option)
                          : form[field] === option;

                        return (
                          <label
                            key={option}
                            className={`group flex min-w-0 cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-3 transition-all duration-200 ${
                              selected
                                ? "border-[#3478C5] bg-[#EFF6FF] ring-1 ring-blue-200"
                                : "border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/40"
                            } ${
                              isRating
                                ? "flex-col justify-center text-center"
                                : ""
                            }`}
                          >
                            <input
                              type={isCheckbox ? "checkbox" : "radio"}
                              name={field}
                              value={option}
                              checked={selected}
                              onChange={(e) => {
                                if (isCheckbox) {
                                  const updated = e.target.checked
                                    ? [...selectedValues, option]
                                    : selectedValues.filter(
                                        (item) => item !== option
                                      );

                                  updateField(field, updated);
                                } else {
                                  updateField(field, option);
                                }
                              }}
                              className="sr-only"
                            />

                            {isRating ? (
                              <>
                                <span className="text-lg leading-none sm:text-xl">
                                  {emojis[optionIndex]}
                                </span>

                                <span
                                  className={`max-w-full break-words text-xs font-semibold ${
                                    selected
                                      ? "text-[#245B9A]"
                                      : "text-slate-600"
                                  }`}
                                >
                                  {translateOption(option)}
                                </span>
                              </>
                            ) : (
                              <>
                                <span
                                  className={`flex h-4 w-4 shrink-0 items-center justify-center border ${
                                    isCheckbox
                                      ? "rounded"
                                      : "rounded-full"
                                  } ${
                                    selected
                                      ? "border-[#3478C5] bg-[#3478C5]"
                                      : "border-slate-300 bg-white"
                                  }`}
                                >
                                  {selected &&
                                    (isCheckbox ? (
                                      <svg
                                        className="h-3 w-3 text-white"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M16.704 5.29a1 1 0 010 1.415l-7.2 7.2a1 1 0 01-1.415 0l-3.6-3.6a1 1 0 011.415-1.415l2.893 2.893 6.493-6.493a1 1 0 011.415 0z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    ) : (
                                      <span className="h-1.5 w-1.5 rounded-full bg-white" />
                                    ))}
                                </span>

                                <span
                                  className={`min-w-0 flex-1 break-words text-sm font-medium leading-5 ${
                                    selected
                                      ? "text-[#245B9A]"
                                      : "text-slate-600"
                                  }`}
                                >
                                  {translateOption(option)}
                                </span>
                              </>
                            )}
                          </label>
                        );
                      })}
                    </div>

                    {field === "q2" && selectedValues.includes("Others") && (
                      <div className="mt-3">
                        <label htmlFor="q2-other" className="mb-1.5 block text-sm font-medium text-slate-700">
                          {t("Please specify", "దయచేసి వివరించండి")} *
                        </label>
                        <input
                          id="q2-other"
                          type="text"
                          value={form.q2Other}
                          onChange={(e) => updateField("q2Other", e.target.value)}
                          placeholder={t("Enter referral source", "క్లినిక్ గురించి తెలిసిన మార్గాన్ని నమోదు చేయండి")}
                          className={inputClass}
                        />
                        {fieldErrors.q2Other && <p className="mt-1 text-xs text-red-600">{fieldErrors.q2Other}</p>}
                      </div>
                    )}

                    {field === "q9" && selectedValues.includes("Others (Specify)") && (
                      <div className="mt-3">
                        <label htmlFor="q9-other" className="mb-1.5 block text-sm font-medium text-slate-700">
                          {t("Please specify the area", "దయచేసి విభాగాన్ని వివరించండి")} *
                        </label>
                        <input
                          id="q9-other"
                          type="text"
                          value={form.q9Other}
                          onChange={(e) => updateField("q9Other", e.target.value)}
                          placeholder={t("Enter the area that needs improvement", "మెరుగుపరచాల్సిన విభాగాన్ని నమోదు చేయండి")}
                          className={inputClass}
                        />
                        {fieldErrors.q9Other && <p className="mt-1 text-xs text-red-600">{fieldErrors.q9Other}</p>}
                      </div>
                    )}

                    {fieldErrors[field] && (
                      <p
                        role="alert"
                        className="mt-2 break-words text-xs font-medium text-red-600"
                      >
                        {fieldErrors[field]}
                      </p>
                    )}
                  </fieldset>
                );
              })}
            </div>

            <p className="mt-4 break-words px-1 text-xs text-slate-500">
              <span className="text-red-500">*</span>{" "}
              {t(
                "All questions are required.",
                "అన్ని ప్రశ్నలకు సమాధానాలు తప్పనిసరి."
              )}
            </p>
          </section>


          {/* ADDITIONAL FEEDBACK & SIGNATURE */}
          <section className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
            <div className="mb-5 flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-sm font-bold text-[#204080]">
                03
              </div>
              <div className="min-w-0">
                <h2 className="break-words font-bold text-slate-900">
                  {t("Additional Feedback", "అదనపు అభిప్రాయం")}
                </h2>
                <p className="text-xs text-slate-500">
                  {t(
                    "Share any suggestions and provide your signature.",
                    "మీ సూచనలను తెలియజేసి, సంతకం చేయండి."
                  )}
                </p>
              </div>
            </div>

            {/* SUGGESTIONS */}
            <div className="mb-6">
              <label
                htmlFor="patient-suggestions"
                className="mb-2 block text-sm font-semibold text-slate-700"
              >
                {t("Suggestions / Additional Comments", "సూచనలు / అదనపు వ్యాఖ్యలు")}
                <span className="ml-2 text-xs font-normal text-slate-400">
                  ({t("Optional", "ఐచ్ఛికం")})
                </span>
              </label>
              <textarea
                id="patient-suggestions"
                rows={4}
                value={form.suggestions}
                onChange={(e) => updateField("suggestions", e.target.value)}
                placeholder={t(
                  "Please share your suggestions to help us improve our services...",
                  "మా సేవలను మెరుగుపరచడానికి మీ సూచనలను తెలియజేయండి..."
                )}
                className={`${inputClass} resize-y`}
              />
            </div>

            {/* PATIENT SIGNATURE */}
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                {t("Patient Signature", "రోగి సంతకం")}
                <span className="ml-1 text-red-500">*</span>
              </label>
              <p className="mb-3 text-xs leading-5 text-slate-500">
                {t(
                  "Please sign in the box below using your finger or mouse.",
                  "దయచేసి క్రింది బాక్స్‌లో మీ వేలితో లేదా మౌస్‌తో సంతకం చేయండి."
                )}
              </p>

              <div className="overflow-hidden rounded-xl border border-dashed border-slate-300 bg-slate-50">
                <canvas
                  ref={canvasRef}
                  width={700}
                  height={180}
                  aria-label={t("Patient signature area", "రోగి సంతకం చేసే స్థలం")}
                  className="block h-36 w-full touch-none cursor-crosshair"
                  onPointerDown={startDrawing}
                  onPointerMove={drawSignature}
                  onPointerUp={stopDrawing}
                  onPointerCancel={stopDrawing}
                  onLostPointerCapture={stopDrawing}
                />
              </div>

              {fieldErrors.signature && (
                <p role="alert" className="mt-2 text-xs font-medium text-red-600">
                  {fieldErrors.signature}
                </p>
              )}

              <div className="mt-3 flex items-center justify-between gap-3">
                <span className="text-xs text-slate-400">
                  {t("Sign above", "పైన సంతకం చేయండి")}
                </span>
                <button
                  type="button"
                  onClick={clearSignature}
                  className="text-xs font-semibold text-red-600 transition hover:text-red-700"
                >
                  {t("Clear Signature", "సంతకాన్ని తొలగించండి")}
                </button>
              </div>
            </div>
          </section>

                    {/* MESSAGE */}
          {message && (
            <div
              role={messageType === "error" ? "alert" : "status"}
              className={`min-w-0 break-words rounded-xl border p-4 text-sm ${
                messageType === "success"
                  ? "border-green-200 bg-green-50 text-green-700"
                  : "border-red-200 bg-red-50 text-red-700"
              }`}
            >
              {message}
            </div>
          )}

        </form>
      </main>

      {/* FIXED BOTTOM ACTION BAR */}
      <footer className="fixed inset-x-0 bottom-0 z-40 w-full border-t border-slate-200 bg-white/95 shadow-[0_-4px_15px_rgba(15,23,42,0.06)] backdrop-blur-md">

        <div className="mx-auto flex w-full max-w-5xl min-w-0 flex-row gap-2 px-3 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:justify-end sm:gap-3 sm:px-6">

          <button
            type="button"
            disabled={saving || fetchingPatient}
            onClick={handleClear}
            className="flex min-h-12 min-w-0 flex-1 items-center justify-center rounded-xl border border-slate-300 bg-white px-2 py-3 text-center text-xs font-semibold leading-5 text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 sm:flex-none sm:px-6 sm:text-sm"
          >
            {t("Clear Form", "ఫారమ్ క్లియర్ చేయండి")}
          </button>

          <button
            type="submit"
            form="patient-feedback-form"
            disabled={saving || fetchingPatient}
            className="flex min-h-12 min-w-0 flex-[1.35] items-center justify-center rounded-xl bg-[#204080] px-2 py-3 text-center text-xs font-semibold leading-5 text-white shadow-sm transition hover:bg-[#2E5AAC] disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none sm:px-7 sm:text-sm"
          >
            {saving
              ? t("Submitting...", "సమర్పిస్తోంది...")
              : t("Submit Feedback", "అభిప్రాయం సమర్పించండి")}
          </button>

        </div>
      </footer>

      {showSuccessModal && (
        <div
          className="fixed inset-0 z-[100] flex min-h-[100dvh] items-center justify-center bg-slate-950/60 px-4 py-6 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="feedback-success-title"
          aria-describedby="feedback-success-description"
        >
          <div className="w-full max-w-md rounded-3xl bg-white px-6 py-8 text-center shadow-2xl sm:px-9 sm:py-10">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50 ring-8 ring-emerald-50/50">
              <svg
                className="h-10 w-10 text-emerald-600"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M7 12.5l3.5 3.5L17.5 9" />
              </svg>
            </div>

            <h2 id="feedback-success-title" className="mt-6 text-xl font-bold leading-tight text-green-800 sm:text-2xl">
              {t("Feedback Submitted Successfully!", "అభిప్రాయం విజయవంతంగా సమర్పించబడింది!")}
            </h2>
            <p id="feedback-success-description" className="mt-3 text-sm leading-6 text-slate-600">
              {t(
                "Thank you for sharing your experience with us. Your feedback has been recorded successfully and will help us improve our services.",
                "మీ అనుభవాన్ని మాతో పంచుకున్నందుకు ధన్యవాదాలు. మీ అభిప్రాయం విజయవంతంగా నమోదు చేయబడింది. మా సేవలను మెరుగుపరచడానికి ఇది మాకు సహాయపడుతుంది."
              )}
            </p>

            <button
              type="button"
              onClick={() => setShowSuccessModal(false)}
              className="mt-7 min-h-12 w-full rounded-xl bg-[#204080] px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#183365] focus:outline-none focus:ring-4 focus:ring-blue-200"
            >
              {t("Done", "సరే")}
            </button>

            <p className="mt-5 text-xs font-medium tracking-wide text-slate-400">
              VPJC – Patient Feedback
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
